import React, {useState} from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import Icon from '@mdi/react';
import SGTitle from 'components/Typography/SGTitle';
import TabPanel from 'components/Tabs/TabPanel';
import Accordion from 'features/POSRegisters/components/Accordion';
import Register from 'features/POSRegisters/interfaces/Register';
import SummaryTabs, {RegisterTabs, tabsDictionary} from './SummaryTabs';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import IpadSummary from './IpadSummary';
import IpadMiniSummary from './IpadMiniSummary';
import ReceiptPrinterSummary from './ReceiptPrinterSummary';
import PaymentTerminalSummary from './PaymentTerminalSummary';
import {mdiCashRegister} from '@mdi/js';
import {SeverityPriorityEnum} from 'features/POSRegisters/interfaces/Severity';
import ScanDetails from 'features/POSRegisters/interfaces/ScanDetails';
import Device from 'features/POSRegisters/interfaces/Device';
import {getDeviceList} from 'features/POSRegisters/functions/getDeviceList';
import {
  checkIsScanFailureAlert,
  checkIsOrphanDeviceAlert,
  checkIsConfigurationDeviceAlert,
  hasAssociation,
} from 'features/POSRegisters/functions/alertTypes';
import UnsupportedSummary from './UnsupportedSummary';
import DeviceType from 'features/POSRegisters/interfaces/DeviceType';

//#region styles
const StyledAccordion = styled(Accordion)`
  border-bottom: 1px solid ${props => props.theme.grey};
  padding-top: 8px;

  > .MuiAccordionSummary-content {
    min-height: 48px;
  }

  > .MuiGrid-root {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StyledRegisterSummaryHeader = styled(Grid)`
  display: grid;
  grid-template-columns: 15rem auto 1fr;
  padding-top: 8px;
  gap: 16px;
  color: ${props => props.theme.disabledStateBlue};
  align-items: center;
`;

const StyledRegisterSummaryHeaderTitle = styled(Grid)`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  align-items: center;
`;

const StyledRegisterSummaryContainer = styled(Grid)`
  display: grid;
  grid-template-columns: 1fr;
  padding-top: 8px;
  gap: 16px;
`;

const StyledDivider = styled.div`
  height: 75%;
  border-left: 1px solid ${props => props.theme.grey};
`;

//#endregion styles

export default function RegisterSummary(register: Register) {
  const [selectedTab, setSelectedTab] = useState<RegisterTabs | undefined>();
  const [expanded, setExpanded] = useState<boolean>(false);

  const hasAlerts = (register: Register) =>
    Number(SeverityPriorityEnum[getPriorityAlert(getDeviceList(register)).toUpperCase() as any]) >
    1;

  const hasAnomalyAlert = (device: Device<ScanDetails>) =>
    device?.alerts.some(
      alert =>
        checkIsScanFailureAlert(alert) ||
        checkIsOrphanDeviceAlert(alert) ||
        checkIsConfigurationDeviceAlert(alert)
    );

  function handleExpandedChange(newValue: boolean) {
    // TODO: To be review
    // if (!expanded && !selectedTab) {
    //   setSelectedTab(tabsDictionary.IPAD_AIR);
    // }
    setExpanded(newValue);
  }

  return (
    <StyledAccordion
      className="transparent"
      header={
        <StyledRegisterSummaryHeader>
          <StyledRegisterSummaryHeaderTitle>
            {hasAlerts(register) ? (
              <SeverityBadge severity={getPriorityAlert(getDeviceList(register))} size={1.125} />
            ) : (
              <Icon path={mdiCashRegister} size={1.125} />
            )}

            <SGTitle className="text-color t5">
              Register {register.ipad?.scanResults?.workstationNumber}{' '}
              {register.id && ` - ${register.id}`}
            </SGTitle>
          </StyledRegisterSummaryHeaderTitle>
          <StyledDivider />
          <SummaryTabs
            selectedTab={selectedTab}
            handleTabChange={tab => setSelectedTab(tab)}
            register={register}
            expanded={expanded}
          />
        </StyledRegisterSummaryHeader>
      }
      expanded={expanded}
      onExpandChange={handleExpandedChange}
    >
      <StyledRegisterSummaryContainer>
        <Grid item xs={12}>
          <TabPanel value={selectedTab} index={tabsDictionary.IPAD_AIR}>
            {register.ipad?.scanResults?.isSupported === false ? (
              <UnsupportedSummary deviceType={DeviceType.IPAD} />
            ) : (
              <IpadSummary {...register.ipad} />
            )}
          </TabPanel>

          <TabPanel value={selectedTab} index={tabsDictionary.IPAD_MINI}>
            {register.ipadMini?.scanResults?.isSupported === false ? (
              <UnsupportedSummary deviceType={DeviceType.IPAD_MINI} />
            ) : (
              <IpadMiniSummary {...register.ipadMini} />
            )}
          </TabPanel>

          <TabPanel value={selectedTab} index={tabsDictionary.RECEIPT_PRINTER}>
            {register.receiptPrinter?.scanResults?.isSupported === false ? (
              <UnsupportedSummary deviceType={DeviceType.RECEIPT_PRINTER} />
            ) : (
              <ReceiptPrinterSummary
                {...register.receiptPrinter}
                barcodeScanner={register.barcodeScanner}
                cashDrawer={register.cashDrawer}
              />
            )}
          </TabPanel>

          <TabPanel value={selectedTab} index={tabsDictionary.PAYMENT_TERMINAL}>
            {register.paymentTerminal?.scanResults?.isSupported === false ? (
              <UnsupportedSummary deviceType={DeviceType.PAYMENT_TERMINAL} />
            ) : (
              <PaymentTerminalSummary {...register.paymentTerminal} />
            )}
          </TabPanel>
        </Grid>
      </StyledRegisterSummaryContainer>
    </StyledAccordion>
  );
}
